<template>
  <the-dialog-box
    ref="dialogBox"
    :submit="submit"
    :max-width="700"
    :submit-btn-text="item.id ? 'Update' : 'Create'"
  >
    <template slot="header">
      <span>Client Address for {{ clientName }}</span>
    </template>
    <template slot="body">
      <v-container
        fluid
        class="py-0"
      >
        <v-row>
          <v-col
            cols="12"
            class="pb-0"
          >
            <div class="text-caption font-weight-bold text-uppercase text--secondary">
              Address Details
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col
            cols="12"
            sm="5"
            class="pt-0 pb-0"
          >
            <v-autocomplete
              v-model="item.type_id"
              :items="types"
              item-text="name"
              item-value="id"
              label="Type"
              autocomplete="preventAutocomplete"
              prepend-icon="mdi-tag-outline"
            >
              <!-- eslint-disable-next-line vue/no-template-shadow -->
              <template #selection="{ item }">
                <v-chip
                  v-if="item"
                  small
                  :color="item.color"
                  text-color="white"
                  class="mr-2"
                >
                  {{ item.name }}
                </v-chip>
              </template>
              <!-- eslint-disable-next-line vue/no-template-shadow -->
              <template #item="{ item }">
                <v-chip
                  v-if="item"
                  small
                  :color="item.color"
                  text-color="white"
                  class="mr-2"
                >
                  {{ item.name }}
                </v-chip>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col
            cols="12"
            sm="7"
            class="pt-0 pb-0"
          >
            <v-text-field
              v-model="item.unit"
              label="Unit / Suite"
              :rules="[(v) => (v === null || v.length <= 45) || 'Max 45 characters']"
              counter="45"
              required
              autocomplete="preventAutocomplete"
              prepend-icon="mdi-door"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            cols="12"
            class="py-0"
          >
            <div class="text-caption font-weight-bold text-uppercase text--secondary">
              Location
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col
            cols="12"
            class="pt-0 pb-0"
          >
            <vuetify-google-autocomplete
              id="google"
              ref="google"
              v-model="googleAddress"
              :rules="[(v) => !!v || 'Address is required']"
              classname="form-control"
              :country="'ca'"
              @placechanged="getAddressData"
            >
              <template slot="label">
                Address<RedAsterisk />
              </template>
            </vuetify-google-autocomplete>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </the-dialog-box>
</template>

<script>
import * as FormatHelpers from '@/helpers/FormatHelpers';
import VuetifyGoogleAutocomplete from 'vuetify-google-autocomplete';

export default {
  components: {
    VuetifyGoogleAutocomplete,
  },
  data: () => ({
    item: null,
    itemId: 0,
    googleAddress: null,
    itemDto: {
      id: 0,
      type_id: null,
      unit: null,
      building: null,
      street: '',
      city: '',
      region_id: 9,
      country_id: 1,
      postal_code: '',
      client_id: null,
    },
    countries: [],
    regions: [],
    clientName: '',
    types: [],
  }),
  created() {
    this.item = { ...this.itemDto };
  },
  methods: {

    init() {
      this.getCountries();
      this.getRegions();
      this.getTypes();
    },

    async get(id) {
      if (!id) return;
      const submitModel = {
        filters: {
          id,
        },
      };
      const res = await this.$api.address.read(submitModel);
      if (!res || res.length < 1 || !res[0]) return;
      const firstIndex = 0;
      this.item = res[firstIndex];
      this.googleAddress = this.item.address;
    },

    async getCountries() {
      this.countries = await this.$api.country.get();
    },
    async getRegions() {
      this.regions = await this.$api.region.get();
    },
    async getTypes() {
      this.types = await this.$api.addressType.get();
    },

    async getAddressData(addressData, placeData) {
      if (this.countries.length === 0) await this.getCountries();
      if (this.regions.length === 0) await this.getRegions();

      const country = this.findCountry(addressData.country);
      const region = this.findRegion(
        addressData.administrative_area_level_1,
        country ? country.id : null,
      );

      this.googleAddress = placeData.formatted_address;
      this.item.building = addressData.street_number || '';
      this.item.street = addressData.route || '';
      this.item.city = addressData.locality || addressData.administrative_area_level_2 || '';
      this.item.region_id = region ? region.id : this.item.region_id;
      this.item.country_id = country
        ? country.id
        : (region ? region.country_id : this.item.country_id);
      this.item.postal_code = addressData.postal_code || '';
    },

    normalizeAddressPart(value) {
      return value ? value.toString().trim().toLowerCase() : '';
    },

    findCountry(countryName) {
      const normalizedCountry = this.normalizeAddressPart(countryName);
      if (!normalizedCountry) return null;

      return this.countries.find((country) => (
        this.normalizeAddressPart(country.name) === normalizedCountry
        || this.normalizeAddressPart(country.abbreviation) === normalizedCountry
        || (normalizedCountry === 'ca' && this.normalizeAddressPart(country.name) === 'canada')
        || (normalizedCountry === 'us' && this.normalizeAddressPart(country.name) === 'united states')
      ));
    },

    findRegion(regionNameOrCode, countryId = null) {
      const normalizedRegion = this.normalizeAddressPart(regionNameOrCode);
      if (!normalizedRegion) return null;

      return this.regions.find((region) => (
        (!countryId || String(region.country_id) === String(countryId))
        && (
          this.normalizeAddressPart(region.name) === normalizedRegion
          || this.normalizeAddressPart(region.abbreviation) === normalizedRegion
        )
      ));
    },

    validateParsedAddress() {
      const missingAddressFields = [];
      if (!this.item.building) missingAddressFields.push('building number');
      if (!this.item.street) missingAddressFields.push('street name');
      if (!this.item.city) missingAddressFields.push('city');
      if (!this.item.region_id) missingAddressFields.push('province or state');
      if (!this.item.country_id) missingAddressFields.push('country');

      if (!missingAddressFields.length) return true;

      const addressErrorMessage = missingAddressFields.length === 5
        ? 'Please select an address.'
        : `Please complete the address: ${missingAddressFields.join(', ')}.`;

      this.$bus.$emit('showError', addressErrorMessage);
      return false;
    },

    async submit() {
      if (!this.item) return false;
      if (!this.validateParsedAddress()) return false;

      let res;
      if (this.item.id) {
        res = await this.$api.address.update(this.item);
      } else {
        const userId = this.$store.getters.user.id;
        if (userId) this.item.created_by = userId;
        res = await this.$api.address.store(this.item);
      }

      // eslint-disable-next-line eqeqeq
      if (res == 'exist') {
        this.$bus.$emit('showError', 'This Address already exists');
        return false;
      }

      if (!res) {
        this.$bus.$emit('showError');
        return false;
      }

      this.$emit('success');
      return res;
    },

    open(clientId, clientName, addressId) {
      this.clientName = clientName || '';
      if (addressId) {
        this.googleAddress = null;
        this.item.id = addressId;
        this.get(addressId);
      } else {
        this.item = { ...this.itemDto };
        this.googleAddress = null;
      }
      this.item.client_id = clientId;
      this.$refs.dialogBox.open();
      this.getCountries();
      this.getRegions();
      this.getTypes();
    },

    formatPhoneNumber() {
      this.item.phone_number = FormatHelpers.formatPhoneNumber(this.item.phone_number);
    },
  },
};
</script>
